import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Zap, CheckCircle, XCircle, Mail, LogIn } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { useAuth } from '@/context/AuthContext';
import BrandLogo from '@/components/BrandLogo';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { logout } = useAuth();
  
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setError('No verification token provided');
        setVerifying(false);
        return;
      }
      
      try {
        const response = await axios.post(`${API}/auth/verify-email?token=${token}`);
        
        if (response.data.success) {
          setVerified(true);
          // Clear all sessions so user must log in fresh
          logout();
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Verification failed');
      } finally {
        setVerifying(false);
      }
    };
    
    verifyEmail();
  }, [token, logout]);

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#2563FF] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="font-body text-[#64748B]">Verifying your email...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center">
            <BrandLogo size="md" />
          </Link>
          <p className="font-body text-[#64748B] mt-4">
            {verified ? 'Email Verified!' : 'Verification Failed'}
          </p>
        </div>

        <div className="glass-pane p-8">
          {verified ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-4"
            >
              <div className="w-16 h-16 bg-green-950/30 border border-green-500/50 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="font-heading text-xl text-[#0F172A] mb-2" data-testid="verify-success-title">You're All Set!</h2>
              <p className="font-body text-[#64748B] text-sm mb-6">
                Your email has been verified. Please sign in to access your account.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 btn-primary px-6 py-3 font-heading"
                data-testid="verify-go-to-login"
              >
                <LogIn className="w-4 h-4" />
                SIGN IN
              </Link>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-4"
            >
              <div className="w-16 h-16 bg-[#EFF6FF] border border-[#2563FF]/50 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-[#2563FF]" />
              </div>
              <h2 className="font-heading text-xl text-[#0F172A] mb-2">Verification Failed</h2>
              <p className="font-body text-[#64748B] text-sm mb-6">
                {error || 'This verification link is invalid or has expired.'}
              </p>
              <div className="space-y-3">
                <Link
                  to="/login"
                  className="block btn-primary px-6 py-3 font-heading text-center"
                >
                  <Mail className="w-4 h-4 inline mr-2" />
                  SIGN IN
                </Link>
                <p className="font-body text-[#64748B] text-xs">
                  Need a new verification link? Sign in and we'll help you.
                </p>
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
